import React from 'react';
import { X, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, XCircle, Hash, Tag, Calendar, Shield } from 'lucide-react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';


const CATEGORY_LABELS = {
  ride_payment: 'Ride Payment',
  delivery_payment: 'Delivery Payment',
  commission: 'Platform Commission',
  withdrawal: 'Withdrawal',
  refund: 'Refund',
  bonus: 'Bonus',
  adjustment: 'Manual Adjustment',
  escrow_hold: 'Escrow Hold',
  escrow_release: 'Escrow Release',
  driver_earnings_transfer: 'Driver Earnings Transfer',
};

const TransactionDetailModal = ({ transaction, currency, onClose }) => {
  if (!transaction) return null;

  const formatAmount = (val) => new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2 })?.format(val || 0);
  const formatDate = (dateStr) => {
    try { return format(new Date(dateStr), 'MMMM d, yyyy · h:mm:ss a'); }
    catch { return dateStr || '—'; }
  };

  const isCredit = transaction?.ledger_type === 'credit';

  const StatusBadge = ({ status }) => {
    const configs = {
      completed: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/20 border-emerald-500/30', label: 'Completed' },
      pending: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/20 border-yellow-500/30', label: 'Pending' },
      failed: { icon: XCircle, color: 'text-rose-400', bg: 'bg-rose-500/20 border-rose-500/30', label: 'Failed' },
    };
    const cfg = configs?.[status] || configs?.pending;
    const Icon = cfg?.icon;
    return (
      <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${cfg?.bg} ${cfg?.color}`}>
        <Icon className="w-4 h-4" />
        {cfg?.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0d1f3c] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-white font-semibold">Transaction Details</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-white/60" />
          </button>
        </div>

        {/* Amount hero */}
        <div className={`p-6 text-center border-b border-white/10 ${isCredit ? 'bg-emerald-500/5' : 'bg-rose-500/5'}`}>
          <div className={`w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center ${isCredit ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
            {isCredit
              ? <ArrowDownLeft className="w-7 h-7 text-emerald-400" />
              : <ArrowUpRight className="w-7 h-7 text-rose-400" />
            }
          </div>
          <p className={`text-3xl font-bold mb-1 ${isCredit ? 'text-emerald-400' : 'text-rose-400'}`}>
            {isCredit ? '+' : '-'}{currency || 'KES'} {formatAmount(transaction?.amount)}
          </p>
          <p className="text-white/50 text-sm">{transaction?.description || 'Transaction'}</p>
        </div>

        {/* Details */}
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-white/40 text-sm">Status</span>
            <StatusBadge status={transaction?.status} />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <Tag className="w-4 h-4" />
              Category
            </div>
            <span className="text-white/70 text-sm font-medium">
              {CATEGORY_LABELS?.[transaction?.category] || transaction?.category}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <Hash className="w-4 h-4" />
              Transaction ID
            </div>
            <span className="text-white/50 text-xs font-mono">{transaction?.id?.slice(0, 16)}...</span>
          </div>

          {transaction?.reference_id && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <Hash className="w-4 h-4" />
                Reference
              </div>
              <span className="text-white/50 text-xs font-mono">{transaction?.reference_id?.slice(0, 16)}...</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <Calendar className="w-4 h-4" />
              Date & Time
            </div>
            <span className="text-white/70 text-xs">{formatDate(transaction?.created_at)}</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <Shield className="w-4 h-4" />
              Type
            </div>
            <span className={`text-sm font-semibold capitalize ${isCredit ? 'text-emerald-400' : 'text-rose-400'}`}>
              {transaction?.ledger_type}
            </span>
          </div>
        </div>

        {/* Escrow note for pending */}
        {transaction?.status === 'pending' && (
          <div className="mx-5 mb-5 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">
            <p className="text-yellow-400/70 text-xs text-center">
              ⏳ This transaction is in escrow — funds will be released upon trip completion
            </p>
          </div>
        )}

        <div className="p-5 pt-0">
          <button
            onClick={onClose}
            className="w-full py-3 bg-white/5 hover:bg-white/10 text-white/60 rounded-xl transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailModal;
